// Pull-to-refresh hook for mobile
import { useState, useRef, useCallback, useEffect } from 'react'

interface PullToRefreshOptions {
  onRefresh: () => Promise<void>
  threshold?: number // Distance to trigger refresh
  enabled?: boolean
}

interface PullToRefreshState {
  isPulling: boolean
  isRefreshing: boolean
  pullDistance: number
}

export function usePullToRefresh(
  ref: React.RefObject<HTMLElement | null>,
  options: PullToRefreshOptions
) {
  const {
    onRefresh,
    threshold = 80,
    enabled = true,
  } = options

  const [state, setState] = useState<PullToRefreshState>({
    isPulling: false,
    isRefreshing: false,
    pullDistance: 0,
  })

  const startY = useRef<number | null>(null)
  const currentY = useRef<number | null>(null)

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!enabled || state.isRefreshing) return
    
    const element = ref.current
    if (!element || element.scrollTop > 0) return
    
    startY.current = e.touches[0].clientY
    setState(prev => ({ ...prev, isPulling: true }))
  }, [enabled, state.isRefreshing, ref])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!enabled || !startY.current || state.isRefreshing) return
    
    const element = ref.current
    if (!element || element.scrollTop > 0) {
      startY.current = null
      setState(prev => ({ ...prev, isPulling: false, pullDistance: 0 }))
      return
    }
    
    currentY.current = e.touches[0].clientY
    const pullDistance = Math.max(0, currentY.current - startY.current)
    
    // Apply resistance - pull distance reduces as you pull further
    const resistedDistance = Math.min(pullDistance * 0.5, threshold * 1.5)
    
    setState(prev => ({ ...prev, pullDistance: resistedDistance }))
  }, [enabled, state.isRefreshing, threshold, ref])

  const handleTouchEnd = useCallback(async () => {
    if (!enabled || state.isRefreshing) return
    
    const pullDistance = state.pullDistance
    
    if (pullDistance >= threshold) {
      setState(prev => ({ ...prev, isRefreshing: true, pullDistance: threshold / 2 }))
      
      try {
        await onRefresh()
      } finally {
        setState({ isPulling: false, isRefreshing: false, pullDistance: 0 })
      }
    } else {
      setState({ isPulling: false, isRefreshing: false, pullDistance: 0 })
    }
    
    startY.current = null
    currentY.current = null
  }, [enabled, state.isRefreshing, state.pullDistance, threshold, onRefresh])

  useEffect(() => {
    const element = ref.current
    if (!element || !enabled) return

    element.addEventListener('touchstart', handleTouchStart, { passive: true })
    element.addEventListener('touchmove', handleTouchMove, { passive: true })
    element.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      element.removeEventListener('touchstart', handleTouchStart)
      element.removeEventListener('touchmove', handleTouchMove)
      element.removeEventListener('touchend', handleTouchEnd)
    }
  }, [ref, enabled, handleTouchStart, handleTouchMove, handleTouchEnd])

  return state
}
